<template>
  <!-- Explore sub-tabs: one pill per zone type. Controlled by `active` (the parent
       renders only the matching section). Full zone-type names + zone/tripwire
       counts; wraps to new rows on narrow screens. -->
  <div class="ztn-wrap">
    <div class="ztn-inner">
      <button
        v-for="item in visibleItems"
        :key="item.id"
        type="button"
        class="ztn-pill"
        :class="{ active: active === item.id && !item.disabled, 'ztn-pill-disabled': item.disabled }"
        :style="{ '--accent': item.accent }"
        :title="item.disabled ? (item.disabledTitle || `${item.title} · not configured`) : item.title"
        :disabled="item.disabled"
        :aria-disabled="item.disabled ? 'true' : null"
        :aria-pressed="active === item.id ? 'true' : 'false'"
        @click="!item.disabled && emit('navigate', item.id)"
      >
        <i v-if="item.icon" :class="['bi', item.icon]"></i>
        <span class="ztn-label">{{ item.title }}</span>
        <span v-if="item.count && item.count.polygon" class="ztn-count">{{ item.count.polygon }}</span>
        <span v-if="item.count && item.count.tripwire" class="ztn-tw">
          <i class="bi bi-arrow-left-right"></i>{{ item.count.tripwire }}
        </span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  // [{ id, title, icon, accent, tripwire, present, disabled, disabledTitle,
  //    count: { polygon, tripwire } }]
  items:  { type: Array, required: true },
  // The currently-open sub-tab id (controlled by the parent).
  active: { type: String, default: '' },
})

const emit = defineEmits(['navigate'])

const visibleItems = computed(() => props.items.filter(i => i.present !== false))
</script>

<style scoped>
.ztn-wrap{
  margin: 0 0 16px;
  padding: 10px 12px;
  border: 1px solid #e6edf3;
  border-radius: 12px;
  box-shadow: 0 3px 12px rgba(13,38,76,.06);
  background: #fff;
}
.ztn-inner{
  display: flex;
  flex-wrap: wrap;        /* wrap pills onto new lines on narrow screens */
  gap: 6px;
}

.ztn-pill{
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid #e6edf3;
  background: #fff;
  color: #425461;
  font-size: .82rem;
  font-weight: 600;
  letter-spacing: .2px;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease, box-shadow .15s ease, transform .05s ease;
  white-space: nowrap;
}
.ztn-pill i{ font-size: .9rem; color: var(--accent); opacity: .9 }
/* Zone count — a small neutral badge. */
.ztn-count{
  font-size: .72rem; font-weight: 700; line-height: 1;
  padding: 2px 6px; border-radius: 999px;
  background: color-mix(in srgb, var(--accent) 12%, #fff);
  color: color-mix(in srgb, var(--accent) 80%, #1a2a36);
}
/* Tripwire count — dashed to match the tripwire visual language. */
.ztn-tw{
  display: inline-flex; align-items: center; gap: 2px;
  font-size: .72rem; font-weight: 700; line-height: 1;
  padding: 1px 5px; border-radius: 999px;
  border: 1px dashed color-mix(in srgb, var(--accent) 55%, #fff);
  color: var(--accent);
}
.ztn-tw i{ font-size: .68rem; color: var(--accent); opacity: 1; }
.ztn-pill:hover{
  border-color: color-mix(in srgb, var(--accent) 35%, #e6edf3);
  background: color-mix(in srgb, var(--accent) 6%, #fff);
}
.ztn-pill:active{ transform: translateY(1px) }
.ztn-pill.active{
  background: color-mix(in srgb, var(--accent) 12%, #fff);
  border-color: color-mix(in srgb, var(--accent) 55%, #fff);
  color: color-mix(in srgb, var(--accent) 75%, #1a2a36);
  box-shadow: 0 2px 6px -2px color-mix(in srgb, var(--accent) 35%, transparent);
}
.ztn-pill.active i:first-child{ opacity: 1 }
.ztn-label{ line-height: 1 }

/* Disabled pill — zone type the surface knows about but isn't configured. */
.ztn-pill-disabled{
  cursor: not-allowed;
  opacity: 0.55;
  border-style: dashed !important;
  background: #f5f7fa;
  color: #94a3b8;
}
.ztn-pill-disabled:hover{ transform: none; box-shadow: none; background: #f5f7fa; }
.ztn-pill-disabled i{ opacity: 0.5; }
</style>
